import React, { useState } from 'react';
import { ArrowLeft, HelpCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/Card';
import { AnswerOptions } from './AnswerOptions';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/utils/cn';
import type { ContextQuestion, QuestionAnswer } from '@/types/frontend';

interface QuestionCardProps {
  question: ContextQuestion;
  onAnswer: (answer: QuestionAnswer) => void;
  selectedAnswer?: QuestionAnswer;
  canGoBack: boolean;
  onGoBack: () => void;
  currentIndex: number;
  totalQuestions: number;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onAnswer,
  selectedAnswer,
  canGoBack,
  onGoBack,
  currentIndex,
  totalQuestions
}) => {
  const [showHelp, setShowHelp] = useState(false);
  
  const handleSelectOption = (optionId: string, customValue?: string) => {
    const answer: QuestionAnswer = {
      question_id: question.id,
      selected_option_id: optionId,
      answered_at: new Date().toISOString()
    };

    if (customValue && question.id === 'pourcentage_murs_isoles_8' && optionId === 'autre_pourcentage') {
      answer.custom_value = customValue;
    }

    onAnswer(answer);
  };
  
  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority.toUpperCase()) {
      case 'CRITIQUE':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'HAUTE':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'MOYENNE':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'BASSE':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };
  
  // Get category label
  const getCategoryLabel = (category: string) => {
    switch (category.toUpperCase()) {
      case 'VERIFICATION':
        return 'Vérification';
      case 'TECHNIQUE':
        return 'Technique';
      case 'BUSINESS':
        return 'Stratégie';
      case 'CONTRAINTES':
        return 'Contraintes';
      case 'PRIORITES':
        return 'Priorités';
      default:
        return category;
    }
  };
  
  return (
    <Card className="w-full animate-fade-in">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Badge
              className={cn(
                'mr-2 text-xs font-medium',
                getPriorityColor(question.priority)
              )}
            >
              Priorité {question.priority}
            </Badge>
            
            <Badge
              variant="outline"
              className="text-xs font-medium"
            >
              {getCategoryLabel(question.category)}
            </Badge>
          </div>
          
          <div className="text-sm text-gray-500">
            Question {currentIndex + 1} / {totalQuestions}
          </div>
        </div>
        
        <CardTitle className="text-xl flex items-center justify-between">
          <span>{question.question}</span>
          {question.description && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-2 p-1"
              onClick={() => setShowHelp(!showHelp)}
            >
              <HelpCircle className="w-5 h-5 text-gray-400" />
            </Button>
          )}
        </CardTitle>
        
        {(showHelp && question.description) && (
          <CardDescription className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
            {question.description}
          </CardDescription>
        )}
        
        {question.required && (
          <div className="mt-2 flex items-center text-sm text-red-600">
            <AlertCircle className="w-4 h-4 mr-1" />
            Réponse requise
          </div>
        )}
      </CardHeader>
      
      <CardContent className="pt-2 pb-6">
        <AnswerOptions
          options={question.options}
          selectedOptionId={selectedAnswer?.selected_option_id}
          onSelect={handleSelectOption}
          questionId={question.id}
        />
        
        {question.impact && (
          <div className="mt-6 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center text-sm text-gray-700">
              <span className="font-medium mr-2">Impact:</span>
              {question.impact}
            </div>
          </div>
        )}
      </CardContent>
      
      {canGoBack && (
        <CardFooter className="pt-4 border-t border-gray-100">
          <Button
            variant="ghost"
            size="sm"
            onClick={onGoBack}
            className="text-gray-600"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Question précédente
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default QuestionCard;